import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", testId: "nav-link-home" },
  { to: "/about", label: "About", testId: "nav-link-about" },
  { to: "/music", label: "Music", testId: "nav-link-music" },
  { to: "/videos", label: "Videos", testId: "nav-link-videos" },
  { to: "/performances", label: "Performances", testId: "nav-link-performances" },
  { to: "/booking", label: "Booking", testId: "nav-link-booking" },
  { to: "/contact", label: "Contact", testId: "nav-link-contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "border-b border-white/[0.06] bg-charcoal-950/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
        <Link to="/" data-testid="nav-brand" className="group flex items-baseline gap-3">
          <span className="font-serif text-xl font-medium tracking-wide text-sand-bright transition-colors duration-300 group-hover:text-gold-light">
            Derrick Hall
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map(({ to, label, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              className={({ isActive }) =>
                `relative font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-mist hover:text-sand-bright"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/booking"
            data-testid="nav-book-derrick-btn"
            className="hidden border border-gold/50 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-charcoal-950 lg:inline-block"
          >
            Book Derrick
          </Link>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="text-sand lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-white/[0.06] bg-charcoal-950/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map(({ to, label, testId }, i) => (
                <motion.div
                  key={to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={to}
                    data-testid={`mobile-${testId}`}
                    className={({ isActive }) =>
                      `block py-3 font-serif text-2xl font-light ${isActive ? "text-gold" : "text-sand"}`
                    }
                  >
                    {label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
